import React, { useEffect, useMemo, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SupplierLayout } from '../../components/SupplierLayout';
import { useSupplierStore, Truck as TruckType } from '../../lib/supplierStore';
import { Truck, Plus, MoreVertical, X, Edit2, Trash2, UserPlus, Wrench, Power, Search, Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle, ChevronLeft, MapPin, Cpu, Star, Phone, ShieldCheck, Copy, History, Briefcase, Navigation, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { RoutePreviewMap } from '../../components/RoutePreviewMap';
const TRUCK_TYPES = ['1.5-Ton Pickup', '3-Ton Box Truck', '5-Ton Curtain-side', 'Flatbed 12m', 'Flatbed recovery', 'Low-bed 40-Ton', 'Refrigerated 3-Ton', 'Refrigerated 7-Ton', 'Tipper / Dump Truck', 'Tanker'];
const STATUS_OPTIONS: TruckType['status'][] = ['Active', 'In service', 'Idle', 'Maintenance'];
// ============ Truck Detail Overlay (Premium) ============
function formatDate(iso?: string | null) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return iso;
  }
}
function monthsBetween(from: string, to: string | null) {
  const start = new Date(from);
  const end = to ? new Date(to) : new Date();
  const diff = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  return Math.max(0, diff);
}
function insuranceStatus(iso?: string) {
  if (!iso) return {
    label: 'Not on file',
    tone: 'neutral' as const,
    days: 0
  };
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (days < 0) return {
    label: 'Expired',
    tone: 'rose' as const,
    days
  };
  if (days <= 30) return {
    label: 'Expiring soon',
    tone: 'amber' as const,
    days
  };
  return {
    label: 'Active',
    tone: 'emerald' as const,
    days
  };
}
function TruckDetailOverlay({
  truck,
  onClose,
  onEdit,
  onAssignDriver
}: {
  truck: TruckType;
  onClose: () => void;
  onEdit: () => void;
  onAssignDriver: () => void;
}) {
  const {
    drivers,
    jobs
  } = useSupplierStore();
  const [mapOpen, setMapOpen] = useState(false);
  const currentDriver = drivers.find(d => d.id === truck.assignedDriverId);
  const jobsForTruck = jobs.filter(j => j.assignedTruckId === truck.id);
  const completedJobs = jobsForTruck.filter(j => j.status === 'completed');
  const ins = insuranceStatus(truck.insuranceValidUntil);
  // Build a unified driver history including the live assignment if missing.
  const history = useMemo(() => {
    const base = (truck.driverHistory || []).slice();
    return base.sort((a, b) => a.from < b.from ? 1 : -1);
  }, [truck.driverHistory]);
  const copyImei = () => {
    if (!truck.gps?.imei) return;
    try {
      navigator.clipboard?.writeText(truck.gps.imei);
      toast.success('IMEI copied to clipboard');
    } catch {
      toast.error('Could not copy IMEI');
    }
  };
  const statusToneMap: Record<TruckType['status'], string> = {
    Active: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    'In service': 'bg-blue-50 text-blue-700 ring-blue-200',
    Idle: 'bg-gray-100 text-gray-700 ring-gray-200',
    Maintenance: 'bg-rose-50 text-rose-700 ring-rose-200'
  };
  const insTone: Record<string, string> = {
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    amber: 'bg-amber-50 text-amber-700 ring-amber-200',
    rose: 'bg-rose-50 text-rose-700 ring-rose-200',
    neutral: 'bg-gray-100 text-gray-600 ring-gray-200'
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.18
  }} className="fixed inset-0 z-50 flex items-stretch" data-id="element-5003">
      <div onClick={onClose} className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" data-id="element-5004" />
      <motion.div initial={{
      x: 60,
      opacity: 0
    }} animate={{
      x: 0,
      opacity: 1
    }} exit={{
      x: 60,
      opacity: 0
    }} transition={{
      type: 'spring',
      stiffness: 320,
      damping: 32
    }} className="relative ml-auto w-full max-w-4xl h-full bg-gray-50 shadow-2xl flex flex-col overflow-hidden" data-id="element-5005">
        {/* Hero */}
        <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-900 to-[#0a2540] text-white p-6 sm:p-8" data-id="element-5006">
          <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-brand-500/20 blur-3xl pointer-events-none" data-id="element-5007" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-blue-500/15 blur-3xl pointer-events-none" data-id="element-5008" />
          <div className="relative" data-id="element-5009">
            <div className="flex items-center justify-between" data-id="element-5010">
              <button onClick={onClose} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 ring-1 ring-white/15 text-xs font-bold transition-colors" data-id="element-5011">
                <ChevronLeft className="w-3.5 h-3.5" data-id="element-5012" /> Back to fleet
              </button>
              <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/10 ring-1 ring-white/15`} data-id="element-5013">
                {truck.status === 'Active' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-5014" />}
                {truck.status}
              </span>
            </div>
            <div className="mt-6 flex items-end justify-between gap-6" data-id="element-5015">
              <div className="min-w-0" data-id="element-5016">
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-gray-400" data-id="element-5017">
                  Vehicle
                </p>
                <h2 className="text-3xl sm:text-5xl font-display font-black tracking-tight mt-1" data-id="element-5018">
                  {truck.plate}
                </h2>
                <p className="text-sm text-gray-300 mt-2" data-id="element-5019">
                  {truck.type} · {truck.capacity}
                </p>
              </div>
              <div className="hidden sm:flex w-24 h-24 rounded-2xl bg-white/10 ring-1 ring-white/15 items-center justify-center shrink-0" data-id="element-5020">
                <Truck className="w-12 h-12 text-white" data-id="element-5021" />
              </div>
            </div>
            {/* Primary actions */}
            <div className="mt-7 grid grid-cols-3 gap-2 sm:gap-3" data-id="element-5022">
              <button onClick={() => setMapOpen(true)} className="flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-2 px-3 py-2.5 rounded-xl bg-white text-gray-900 hover:bg-gray-100 transition-colors text-xs font-bold" data-id="element-5023">
                <MapPin className="w-4 h-4" data-id="element-5024" />
                Locate on map
              </button>
              <button onClick={onAssignDriver} className="flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-2 px-3 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 ring-1 ring-white/15 transition-colors text-xs font-bold" data-id="element-5025">
                <UserPlus className="w-4 h-4" data-id="element-5026" />
                {currentDriver ? 'Reassign driver' : 'Assign driver'}
              </button>
              <button onClick={onEdit} className="flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-2 px-3 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 ring-1 ring-white/15 transition-colors text-xs font-bold" data-id="element-5027">
                <Edit2 className="w-4 h-4" data-id="element-5028" />
                Edit vehicle
              </button>
            </div>
          </div>
        </div>
        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto scrollbar-hide p-6 sm:p-8 space-y-6" data-id="element-5029">
          {/* Specs grid */}
          <section data-id="element-5030">
            <h3 className="text-[10px] uppercase tracking-[0.22em] font-bold text-gray-400 mb-3" data-id="element-5031">
              Vehicle identity
            </h3>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4" data-id="element-5032">
              <SpecRow label="Plate" value={truck.plate} data-id="element-5033" />
              <SpecRow label="Type" value={truck.type} data-id="element-5034" />
              <SpecRow label="Capacity" value={truck.capacity} data-id="element-5035" />
              <SpecRow label="VIN" value={truck.vin || '—'} mono data-id="element-5036" />
              <SpecRow label="Last service" value={formatDate(truck.lastService)} data-id="element-5037" />
              <SpecRow label="Status" value={truck.status} data-id="element-5038" />
            </div>
          </section>
          {/* Insurance + GPS row */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5039">
            {/* Insurance */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5" data-id="element-5040">
              <div className="flex items-center justify-between mb-3" data-id="element-5041">
                <div className="flex items-center gap-2" data-id="element-5042">
                  <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center" data-id="element-5043">
                    <ShieldCheck className="w-4 h-4" data-id="element-5044" />
                  </div>
                  <p className="text-sm font-bold text-gray-900" data-id="element-5045">Insurance</p>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ring-1 ${insTone[ins.tone]}`} data-id="element-5046">
                  {ins.label}
                </span>
              </div>
              <p className="text-2xl font-display font-black text-gray-900 leading-none mt-1" data-id="element-5047">
                {formatDate(truck.insuranceValidUntil)}
              </p>
              <p className="text-[11px] text-gray-500 mt-2" data-id="element-5048">
                {ins.tone === 'rose' ? `Expired ${Math.abs(ins.days)}d ago` : ins.tone === 'amber' ? `Renew in ${ins.days}d` : ins.tone === 'emerald' ? `Valid for ${ins.days}d` : 'No insurance record on file'}
              </p>
            </div>
            {/* GPS device */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5" data-id="element-5049">
              <div className="flex items-center justify-between mb-3" data-id="element-5050">
                <div className="flex items-center gap-2" data-id="element-5051">
                  <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center" data-id="element-5052">
                    <Cpu className="w-4 h-4" data-id="element-5053" />
                  </div>
                  <p className="text-sm font-bold text-gray-900" data-id="element-5054">GPS device</p>
                </div>
                {truck.location ? <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" data-id="element-5055">
                    <Wifi className="w-3 h-3" data-id="element-5056" />
                    Online
                  </span> : <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-slate-100 text-slate-700 ring-1 ring-slate-200" data-id="element-5057">
                    <WifiOff className="w-3 h-3" data-id="element-5058" />
                    Offline
                  </span>}
              </div>
              {truck.gps ? <>
                  <p className="text-lg font-display font-black text-gray-900" data-id="element-5059">
                    {truck.gps.brand}{' '}
                    <span className="text-gray-500 font-bold" data-id="element-5060">
                      {truck.gps.model}
                    </span>
                  </p>
                  <div className="mt-3 flex items-center gap-2" data-id="element-5061">
                    <code className="flex-1 text-[11px] font-mono bg-gray-50 border border-gray-100 rounded-lg px-2 py-1.5 text-gray-700" data-id="element-5062">
                      IMEI · {truck.gps.imei}
                    </code>
                    <button onClick={copyImei} className="px-2.5 py-1.5 rounded-lg bg-gray-900 hover:bg-black text-white text-[11px] font-bold inline-flex items-center gap-1 transition-colors" data-id="element-5063">
                      <Copy className="w-3 h-3" data-id="element-5064" />
                      Copy
                    </button>
                  </div>
                </> : <p className="text-sm text-gray-500" data-id="element-5065">
                  No GPS device installed.
                </p>}
            </div>
          </section>
          {/* Current driver */}
          <section data-id="element-5066">
            <h3 className="text-[10px] uppercase tracking-[0.22em] font-bold text-gray-400 mb-3" data-id="element-5067">
              Current driver
            </h3>
            {currentDriver ? <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4" data-id="element-5068">
                <div className="w-12 h-12 rounded-2xl bg-brand-50 text-brand-600 flex items-center justify-center text-base font-display font-black shrink-0" data-id="element-5069">
                  {currentDriver.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
                </div>
                <div className="flex-1 min-w-0" data-id="element-5070">
                  <p className="text-base font-bold text-gray-900 truncate" data-id="element-5071">
                    {currentDriver.name}
                  </p>
                  <div className="flex items-center gap-3 mt-0.5 text-[11px] text-gray-500" data-id="element-5072">
                    <span className="inline-flex items-center gap-1" data-id="element-5073">
                      <Star className="w-3 h-3 text-amber-500" data-id="element-5074" />
                      {currentDriver.rating}
                    </span>
                    <span data-id="element-5075">License {currentDriver.license}</span>
                  </div>
                </div>
                <a href={`tel:${currentDriver.phone.replace(/\s/g, '')}`} className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold ring-1 ring-emerald-200 transition-colors" data-id="element-5076">
                  <Phone className="w-3.5 h-3.5" data-id="element-5077" />
                  {currentDriver.phone}
                </a>
                <button onClick={onAssignDriver} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold transition-colors" data-id="element-5078">
                  <UserPlus className="w-3.5 h-3.5" data-id="element-5079" />
                  Reassign
                </button>
              </div> : <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-6 text-center" data-id="element-5080">
                <div className="w-10 h-10 rounded-xl bg-gray-100 text-gray-400 mx-auto flex items-center justify-center mb-2" data-id="element-5081">
                  <UserPlus className="w-5 h-5" data-id="element-5082" />
                </div>
                <p className="text-sm font-bold text-gray-900" data-id="element-5083">
                  No driver assigned
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5 mb-3" data-id="element-5084">
                  Assign a driver to dispatch this vehicle on jobs.
                </p>
                <button onClick={onAssignDriver} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gray-900 hover:bg-black text-white text-xs font-bold transition-colors" data-id="element-5085">
                  <UserPlus className="w-3.5 h-3.5" data-id="element-5086" />
                  Assign driver
                </button>
              </div>}
          </section>
          {/* Driver history */}
          <section data-id="element-5087">
            <h3 className="text-[10px] uppercase tracking-[0.22em] font-bold text-gray-400 mb-3 flex items-center gap-2" data-id="element-5088">
              <History className="w-3 h-3" data-id="element-5089" />
              Driver history
            </h3>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5" data-id="element-5090">
              {history.length === 0 ? <p className="text-sm text-gray-500" data-id="element-5091">
                  No previous assignments recorded.
                </p> : <ol className="space-y-3 relative" data-id="element-5092">
                  <div className="absolute left-[11px] top-1 bottom-1 w-px bg-gray-100" data-id="element-5093" />
                  {history.map((h, i) => {
                const d = drivers.find(x => x.id === h.driverId);
                const isCurrent = h.to === null;
                const months = monthsBetween(h.from, h.to);
                return <li key={`${h.driverId}-${h.from}`} className="relative flex items-start gap-3 pl-7" data-id="element-5094">
                        <span className={`absolute left-0 top-1 w-[22px] h-[22px] rounded-full flex items-center justify-center ${isCurrent ? 'bg-emerald-500 text-white ring-4 ring-emerald-100' : 'bg-gray-200 text-gray-500'}`} data-id="element-5095">
                          {isCurrent ? <span className="w-1.5 h-1.5 rounded-full bg-white" data-id="element-5096" /> : <span className="text-[10px] font-bold" data-id="element-5097">
                              {history.length - i}
                            </span>}
                        </span>
                        <div className="flex-1 min-w-0 flex items-center justify-between gap-3" data-id="element-5098">
                          <div className="min-w-0" data-id="element-5099">
                            <p className="text-sm font-bold text-gray-900 truncate" data-id="element-5100">
                              {d?.name || 'Unknown driver'}
                              {isCurrent && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 align-middle" data-id="element-5101">
                                  Current
                                </span>}
                            </p>
                            <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-5102">
                              {formatDate(h.from)} →{' '}
                              {h.to ? formatDate(h.to) : 'Present'}
                            </p>
                          </div>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 shrink-0" data-id="element-5103">
                            {months || '<1'} {months === 1 ? 'month' : 'months'}
                          </span>
                        </div>
                      </li>;
              })}
                </ol>}
            </div>
          </section>
          {/* Jobs */}
          <section data-id="element-5104">
            <h3 className="text-[10px] uppercase tracking-[0.22em] font-bold text-gray-400 mb-3 flex items-center gap-2" data-id="element-5105">
              <Briefcase className="w-3 h-3" data-id="element-5106" />
              Jobs completed with this vehicle
              <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 tracking-normal normal-case" data-id="element-5107">
                {completedJobs.length}
              </span>
            </h3>
            {completedJobs.length === 0 ? <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-center" data-id="element-5108">
                <Briefcase className="w-7 h-7 text-gray-300 mx-auto mb-2" data-id="element-5109" />
                <p className="text-sm font-bold text-gray-900" data-id="element-5110">
                  No completed jobs yet
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-5111">
                  Once this vehicle finishes a dispatched job, it'll show up
                  here.
                </p>
              </div> : <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden divide-y divide-gray-100" data-id="element-5112">
                {completedJobs.map(j => <div key={j.id} className="p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors" data-id="element-5113">
                    <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0" data-id="element-5114">
                      <CheckCircle2 className="w-4 h-4" data-id="element-5115" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-5116">
                      <p className="text-sm font-bold text-gray-900 truncate" data-id="element-5117">
                        {j.serviceType}
                      </p>
                      <p className="text-[11px] text-gray-500 truncate" data-id="element-5118">
                        {j.customerName || 'Customer'} ·{' '}
                        {formatDate(j.scheduledDate)} · {j.id}
                      </p>
                    </div>
                    <span className="text-sm font-display font-black text-gray-900" data-id="element-5119">
                      AED {j.price.toLocaleString()}
                    </span>
                  </div>)}
              </div>}
          </section>
        </div>
        {/* Locate-on-map sub-overlay */}
        <AnimatePresence data-id="element-5120">
          {mapOpen && <TruckMapOverlay truck={truck} onClose={() => setMapOpen(false)} data-id="element-5121" />}
        </AnimatePresence>
      </motion.div>
    </motion.div>;
}
function SpecRow({
  label,
  value,
  mono
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return <div className="min-w-0" data-id="element-5122">
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400" data-id="element-5123">
        {label}
      </p>
      <p className={`text-sm font-bold text-gray-900 mt-0.5 truncate ${mono ? 'font-mono' : ''}`} data-id="element-5124">
        {value}
      </p>
    </div>;
}
function TruckMapOverlay({
  truck,
  onClose
}: {
  truck: TruckType;
  onClose: () => void;
}) {
  const lat = truck.location?.lat;
  const lng = truck.location?.lng;
  const gmapsHref = lat && lng ? `https://www.google.com/maps?q=${lat},${lng}` : null;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.18
  }} className="absolute inset-0 z-50 bg-white flex flex-col" data-id="element-5125">
      <div className="shrink-0 px-5 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-5126">
        <button onClick={onClose} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold transition-colors" data-id="element-5127">
          <ChevronLeft className="w-3.5 h-3.5" data-id="element-5128" />
          Back to vehicle
        </button>
        <div className="text-right" data-id="element-5129">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400" data-id="element-5130">
            Vehicle
          </p>
          <p className="text-sm font-display font-black text-gray-900" data-id="element-5131">
            {truck.plate}
          </p>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-hide p-5 sm:p-6 bg-gray-50 space-y-4" data-id="element-5132">
        {lat && lng ? <>
            <RoutePreviewMap pickup={`${lat.toFixed(4)}, ${lng.toFixed(4)}`} singleLocation data-id="element-5133" />
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4" data-id="element-5134">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0" data-id="element-5135">
                <MapPin className="w-5 h-5" data-id="element-5136" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-5137">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400" data-id="element-5138">
                  Last reported position
                </p>
                <p className="text-sm font-bold text-gray-900 mt-0.5" data-id="element-5139">
                  {lat.toFixed(5)}, {lng.toFixed(5)}
                </p>
              </div>
              {gmapsHref && <a href={gmapsHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-900 hover:bg-black text-white text-xs font-bold transition-colors" data-id="element-5140">
                  <Navigation className="w-3.5 h-3.5" data-id="element-5141" />
                  Open in Maps
                </a>}
            </div>
          </> : <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-8 text-center" data-id="element-5142">
            <WifiOff className="w-7 h-7 text-gray-300 mx-auto mb-2" data-id="element-5143" />
            <p className="text-sm font-bold text-gray-900" data-id="element-5144">
              No live location available
            </p>
            <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-5145">
              The GPS device hasn't reported a position recently.
            </p>
          </div>}
      </div>
    </motion.div>;
}
export function SupplierTrucks() {
  const {
    trucks,
    drivers,
    addTruck,
    updateTruck,
    deleteTruck
  } = useSupplierStore();
  const [modalMode, setModalMode] = useState<'add' | 'edit' | null>(null);
  const [editingTruck, setEditingTruck] = useState<TruckType | null>(null);
  const [assignTruck, setAssignTruck] = useState<TruckType | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<TruckType | null>(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [detailTruckId, setDetailTruckId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const filteredTrucks = useMemo(() => {
    return trucks.filter(t => {
      const matchesSearch = search === '' || t.plate.toLowerCase().includes(search.toLowerCase()) || t.type.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [trucks, search, statusFilter]);
  const handleOpenAdd = () => {
    setEditingTruck(null);
    setModalMode('add');
  };
  const handleOpenEdit = (truck: TruckType) => {
    setEditingTruck(truck);
    setModalMode('edit');
  };
  const handleCloseModal = () => {
    setModalMode(null);
    setEditingTruck(null);
  };
  const handleSave = (data: Omit<TruckType, 'id'>) => {
    if (modalMode === 'edit' && editingTruck) {
      updateTruck(editingTruck.id, data);
      toast.success(`Truck ${data.plate} updated`);
    } else {
      addTruck(data);
      toast.success(`Truck ${data.plate} added to fleet`);
    }
    handleCloseModal();
  };
  const handleDelete = () => {
    if (!confirmDelete) return;
    deleteTruck(confirmDelete.id);
    toast.success(`Truck ${confirmDelete.plate} removed from fleet`);
    setConfirmDelete(null);
  };
  const handleAssignDriver = (truckId: string, driverId: string | null) => {
    updateTruck(truckId, {
      assignedDriverId: driverId || undefined
    });
    const driverName = driverId ? drivers.find(d => d.id === driverId)?.name : null;
    toast.success(driverName ? `Assigned ${driverName}` : 'Driver removed from truck');
    setAssignTruck(null);
  };
  const handleChangeStatus = (truck: TruckType, status: TruckType['status']) => {
    updateTruck(truck.id, {
      status
    });
    toast.success(`Status set to ${status}`);
  };
  return <SupplierLayout data-id="element-5146">
      <div className="max-w-6xl mx-auto" data-id="element-5147">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4" data-id="element-5148">
          <div data-id="element-5149">
            <h1 className="text-2xl font-display font-bold text-gray-900" data-id="element-5150">
              Fleet Management
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-5151">
              {trucks.length} total vehicle{trucks.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-5152">
            <button onClick={() => setBulkUploadOpen(true)} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 rounded-xl text-sm font-bold transition-colors shadow-sm" data-id="element-5153">
              <Upload className="w-4 h-4" data-id="element-5154" /> Bulk Upload
            </button>
            <button onClick={handleOpenAdd} className="flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-sm font-bold transition-colors shadow-sm" data-id="element-5155">
              <Plus className="w-4 h-4" data-id="element-5156" /> Add Truck
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6" data-id="element-5157">
          <div className="relative flex-1" data-id="element-5158">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-5159" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by plate or type..." className="w-full pl-9 pr-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-gray-900 focus:border-gray-900" data-id="element-5160" />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:ring-gray-900 focus:border-gray-900" data-id="element-5161">
            <option value="all" data-id="element-5162">All statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s} data-id="element-5163">
                {s}
              </option>)}
          </select>
        </div>

        {filteredTrucks.length === 0 ? <div className="bg-white rounded-2xl p-12 border border-gray-100 text-center" data-id="element-5164">
            <Truck className="w-10 h-10 text-gray-300 mx-auto mb-3" data-id="element-5165" />
            <p className="text-sm font-medium text-gray-900" data-id="element-5166">No trucks found</p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-5167">
              {trucks.length === 0 ? 'Add your first truck to start managing your fleet.' : 'Try adjusting your search or filters.'}
            </p>
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-5168">
            {filteredTrucks.map(truck => {
          const driver = drivers.find(d => d.id === truck.assignedDriverId);
          return <TruckCard key={truck.id} truck={truck} driverName={driver?.name} onClick={() => setDetailTruckId(truck.id)} onEdit={() => handleOpenEdit(truck)} onAssignDriver={() => setAssignTruck(truck)} onChangeStatus={status => handleChangeStatus(truck, status)} onDelete={() => setConfirmDelete(truck)} data-id="element-5169" />;
        })}
          </div>}
      </div>

      {/* Add / Edit Modal */}
      <AnimatePresence data-id="element-5170">
        {modalMode && <TruckFormModal mode={modalMode} initial={editingTruck} drivers={drivers} trucks={trucks} onClose={handleCloseModal} onSave={handleSave} data-id="element-5171" />}
        {assignTruck && <AssignDriverModal truck={assignTruck} drivers={drivers} trucks={trucks} onClose={() => setAssignTruck(null)} onAssign={driverId => handleAssignDriver(assignTruck.id, driverId)} data-id="element-5172" />}
        {confirmDelete && <ConfirmDeleteModal truck={confirmDelete} onCancel={() => setConfirmDelete(null)} onConfirm={handleDelete} data-id="element-5173" />}
        {bulkUploadOpen && <BulkUploadModal onClose={() => setBulkUploadOpen(false)} onImport={rows => {
        let count = 0;
        rows.forEach(row => {
          addTruck({
            plate: row.plate,
            type: row.type,
            capacity: row.capacity,
            status: row.status,
            lastService: row.lastService
          });
          count++;
        });
        toast.success(`${count} truck${count !== 1 ? 's' : ''} imported successfully`);
        setBulkUploadOpen(false);
      }} data-id="element-5174" />}
        {detailTruckId && (() => {
        const t = trucks.find(x => x.id === detailTruckId);
        if (!t) return null;
        return <TruckDetailOverlay truck={t} onClose={() => setDetailTruckId(null)} onEdit={() => {
          setDetailTruckId(null);
          handleOpenEdit(t);
        }} onAssignDriver={() => {
          setDetailTruckId(null);
          setAssignTruck(t);
        }} data-id="element-5175" />;
      })()}
      </AnimatePresence>
    </SupplierLayout>;
}
// ============ Truck Card with Kebab Menu ============
function TruckCard({
  truck,
  driverName,
  onClick,
  onEdit,
  onAssignDriver,
  onChangeStatus,
  onDelete
}: {
  truck: TruckType;
  driverName?: string;
  onClick: () => void;
  onEdit: () => void;
  onAssignDriver: () => void;
  onChangeStatus: (status: TruckType['status']) => void;
  onDelete: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setStatusMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);
  const statusBadgeClass = truck.status === 'Active' ? 'bg-green-100 text-green-700' : truck.status === 'In service' ? 'bg-blue-100 text-blue-700' : truck.status === 'Idle' ? 'bg-gray-100 text-gray-600' : 'bg-red-100 text-red-700';
  return <div onClick={onClick} className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm relative cursor-pointer hover:border-gray-300 hover:shadow-md transition-all" data-id="element-5176">
      <div ref={menuRef} className="absolute top-4 right-4" onClick={e => e.stopPropagation()} data-id="element-5177">
        <button onClick={e => {
        e.stopPropagation();
        setMenuOpen(!menuOpen);
      }} className="p-1.5 text-gray-400 hover:text-gray-900 rounded-lg hover:bg-gray-50 transition-colors" aria-label="Truck options" data-id="element-5178">
          <MoreVertical className="w-5 h-5" data-id="element-5179" />
        </button>
        <AnimatePresence data-id="element-5180">
          {menuOpen && <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: -4
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: -4
        }} transition={{
          duration: 0.12
        }} className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-20" data-id="element-5181">
              <button onClick={() => {
            setMenuOpen(false);
            onEdit();
          }} className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-5182">
                <Edit2 className="w-4 h-4 text-gray-400" data-id="element-5183" /> Edit truck
              </button>
              <button onClick={() => {
            setMenuOpen(false);
            onAssignDriver();
          }} className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-5184">
                <UserPlus className="w-4 h-4 text-gray-400" data-id="element-5185" />
                {driverName ? 'Reassign driver' : 'Assign driver'}
              </button>

              <div className="relative" data-id="element-5186">
                <button onClick={() => setStatusMenuOpen(!statusMenuOpen)} className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-5187">
                  <Power className="w-4 h-4 text-gray-400" data-id="element-5188" /> Change status
                </button>
                <AnimatePresence data-id="element-5189">
                  {statusMenuOpen && <motion.div initial={{
                opacity: 0,
                x: -4
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -4
              }} className="bg-gray-50 border-t border-b border-gray-100" data-id="element-5190">
                      {STATUS_OPTIONS.map(status => <button key={status} onClick={() => {
                  onChangeStatus(status);
                  setStatusMenuOpen(false);
                  setMenuOpen(false);
                }} className={`w-full text-left flex items-center justify-between gap-2 pl-9 pr-3 py-2 text-sm hover:bg-gray-100 ${truck.status === status ? 'font-bold text-gray-900' : 'text-gray-600'}`} data-id="element-5191">
                          {status}
                          {truck.status === status && <span className="text-[10px] text-brand-600 font-bold" data-id="element-5192">
                              ✓
                            </span>}
                        </button>)}
                    </motion.div>}
                </AnimatePresence>
              </div>

              <button onClick={() => {
            setMenuOpen(false);
            onChangeStatus('Maintenance');
          }} className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-5193">
                <Wrench className="w-4 h-4 text-gray-400" data-id="element-5194" /> Send to maintenance
              </button>
              <div className="border-t border-gray-100" data-id="element-5195">
                <button onClick={() => {
              setMenuOpen(false);
              onDelete();
            }} className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50" data-id="element-5196">
                  <Trash2 className="w-4 h-4" data-id="element-5197" /> Remove truck
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      <div className="flex items-center gap-4 mb-4" data-id="element-5198">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${truck.status === 'Active' ? 'bg-brand-50 text-brand-600' : 'bg-gray-100 text-gray-500'}`} data-id="element-5199">
          <Truck className="w-6 h-6" data-id="element-5200" />
        </div>
        <div className="min-w-0" data-id="element-5201">
          <h3 className="font-bold text-gray-900 text-lg truncate" data-id="element-5202">
            {truck.plate}
          </h3>
          <p className="text-xs text-gray-500 truncate" data-id="element-5203">
            {truck.type} • {truck.capacity}
          </p>
        </div>
      </div>

      <div className="space-y-3 pt-4 border-t border-gray-100" data-id="element-5204">
        <div className="flex justify-between items-center text-sm" data-id="element-5205">
          <span className="text-gray-500" data-id="element-5206">Status</span>
          <span className={`font-bold text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md ${statusBadgeClass}`} data-id="element-5207">
            {truck.status}
          </span>
        </div>
        <div className="flex justify-between items-center text-sm" data-id="element-5208">
          <span className="text-gray-500" data-id="element-5209">Driver</span>
          <span className="font-medium text-gray-900 truncate ml-2" data-id="element-5210">
            {driverName || <span className="text-gray-400 italic" data-id="element-5211">Unassigned</span>}
          </span>
        </div>
        <div className="flex justify-between items-center text-sm" data-id="element-5212">
          <span className="text-gray-500" data-id="element-5213">Last Service</span>
          <span className="font-medium text-gray-900" data-id="element-5214">{truck.lastService}</span>
        </div>
      </div>
    </div>;
}
// ============ Add / Edit Modal ============
function TruckFormModal({
  mode,
  initial,
  drivers,
  trucks,
  onClose,
  onSave
}: {
  mode: 'add' | 'edit';
  initial: TruckType | null;
  drivers: ReturnType<typeof useSupplierStore>['drivers'];
  trucks: TruckType[];
  onClose: () => void;
  onSave: (data: Omit<TruckType, 'id'>) => void;
}) {
  const {
    services,
    autoDispatch,
    updateAutoDispatch
  } = useSupplierStore();
  const [plate, setPlate] = useState(initial?.plate || '');
  const [type, setType] = useState(initial?.type || TRUCK_TYPES[0]);
  const [capacity, setCapacity] = useState(initial?.capacity || '');
  const [status, setStatus] = useState<TruckType['status']>(initial?.status || 'Idle');
  const [lastService, setLastService] = useState(initial?.lastService || new Date().toISOString().slice(0, 10));
  const [assignedDriverId, setAssignedDriverId] = useState<string>(initial?.assignedDriverId || '');
  const [driverSearch, setDriverSearch] = useState('');
  const [driverDropdownOpen, setDriverDropdownOpen] = useState(false);
  const driverDropdownRef = useRef<HTMLDivElement>(null);
  // Drivers already assigned to other trucks
  const availableDrivers = drivers.filter(d => !d.assignedTruckId || d.assignedTruckId === initial?.id || d.id === initial?.assignedDriverId);
  const filteredDrivers = availableDrivers.filter(d => d.name.toLowerCase().includes(driverSearch.toLowerCase()));
  const selectedDriver = drivers.find(d => d.id === assignedDriverId);
  // Close driver dropdown on outside click
  useEffect(() => {
    if (!driverDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (driverDropdownRef.current && !driverDropdownRef.current.contains(e.target as Node)) {
        setDriverDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [driverDropdownOpen]);
  // Services for this truck's type (from auto-dispatch capability map)
  const truckServices = autoDispatch.truckServiceMap?.[type] || [];
  const toggleService = (service: string) => {
    const current = autoDispatch.truckServiceMap?.[type] || [];
    const next = current.includes(service) ? current.filter(s => s !== service) : [...current, service];
    updateAutoDispatch({
      truckServiceMap: {
        ...(autoDispatch.truckServiceMap || {}),
        [type]: next
      }
    });
  };
  const handleSubmit = () => {
    if (!plate.trim()) {
      toast.error('Please enter a plate number');
      return;
    }
    if (!capacity.trim()) {
      toast.error('Please enter capacity');
      return;
    }
    onSave({
      plate: plate.trim().toUpperCase(),
      type,
      capacity: capacity.trim(),
      status,
      lastService,
      assignedDriverId: assignedDriverId || undefined
    });
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" data-id="element-5215">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]" data-id="element-5216">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 shrink-0" data-id="element-5217">
          <div data-id="element-5218">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-5219">
              {mode === 'edit' ? 'Edit Truck' : 'Add Truck'}
            </h3>
            <p className="text-sm text-gray-500" data-id="element-5220">
              {mode === 'edit' ? 'Update truck details and assignments' : 'Register a new vehicle to your fleet'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" data-id="element-5221">
            <X className="w-5 h-5" data-id="element-5222" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-4 flex-1" data-id="element-5223">
          <div data-id="element-5224">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5225">
              Plate Number
            </label>
            <input type="text" value={plate} onChange={e => setPlate(e.target.value)} placeholder="e.g., D 12345" className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-5226" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-5227">
            <div data-id="element-5228">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5229">
                Truck Type
              </label>
              <select value={type} onChange={e => setType(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-5230">
                {TRUCK_TYPES.map(t => <option key={t} value={t} data-id="element-5231">
                    {t}
                  </option>)}
              </select>
            </div>
            <div data-id="element-5232">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5233">
                Capacity
              </label>
              <input type="text" value={capacity} onChange={e => setCapacity(e.target.value)} placeholder="e.g., 3 Tons" className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-5234" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-5235">
            <div data-id="element-5236">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5237">
                Status
              </label>
              <select value={status} onChange={e => setStatus(e.target.value as TruckType['status'])} className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-5238">
                {STATUS_OPTIONS.map(s => <option key={s} value={s} data-id="element-5239">
                    {s}
                  </option>)}
              </select>
            </div>
            <div data-id="element-5240">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5241">
                Last Service Date
              </label>
              <input type="date" value={lastService} onChange={e => setLastService(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-5242" />
            </div>
          </div>

          {/* Searchable Driver Picker */}
          <div ref={driverDropdownRef} className="relative" data-id="element-5243">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5244">
              Assign Driver (optional)
            </label>
            {selectedDriver && !driverDropdownOpen ? <button type="button" onClick={() => {
            setDriverDropdownOpen(true);
            setDriverSearch('');
          }} className="w-full flex items-center gap-3 px-3 py-2 border border-gray-300 rounded-xl text-left hover:border-gray-400 transition-colors" data-id="element-5245">
                <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center font-bold text-sm shrink-0" data-id="element-5246">
                  {selectedDriver.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0" data-id="element-5247">
                  <p className="text-sm font-medium text-gray-900 truncate" data-id="element-5248">
                    {selectedDriver.name}
                  </p>
                  <p className="text-xs text-gray-500 truncate" data-id="element-5249">
                    {selectedDriver.phone} • ★ {selectedDriver.rating}
                  </p>
                </div>
                <button type="button" onClick={e => {
              e.stopPropagation();
              setAssignedDriverId('');
            }} className="p-1 text-gray-400 hover:text-red-500 rounded shrink-0" aria-label="Remove driver" data-id="element-5250">
                  <X className="w-4 h-4" data-id="element-5251" />
                </button>
              </button> : <>
                <div className="relative" data-id="element-5252">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-5253" />
                  <input type="text" value={driverSearch} onChange={e => setDriverSearch(e.target.value)} onFocus={() => setDriverDropdownOpen(true)} placeholder="Search drivers by name..." className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-5254" />
                </div>
                <AnimatePresence data-id="element-5255">
                  {driverDropdownOpen && <motion.div initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -4
              }} transition={{
                duration: 0.12
              }} className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-lg" data-id="element-5256">
                      <button type="button" onClick={() => {
                  setAssignedDriverId('');
                  setDriverDropdownOpen(false);
                }} className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 border-b border-gray-100" data-id="element-5257">
                        Unassigned
                      </button>
                      {filteredDrivers.length === 0 ? <div className="px-3 py-4 text-center text-sm text-gray-500" data-id="element-5258">
                          No matching drivers
                        </div> : filteredDrivers.map(d => {
                  const isCurrent = d.id === assignedDriverId;
                  return <button key={d.id} type="button" onClick={() => {
                    setAssignedDriverId(d.id);
                    setDriverDropdownOpen(false);
                  }} className={`w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-gray-50 ${isCurrent ? 'bg-brand-50/40' : ''}`} data-id="element-5259">
                              <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center font-bold text-xs shrink-0" data-id="element-5260">
                                {d.name.charAt(0)}
                              </div>
                              <div className="flex-1 min-w-0" data-id="element-5261">
                                <p className="text-sm font-medium text-gray-900 truncate" data-id="element-5262">
                                  {d.name}
                                </p>
                                <p className="text-xs text-gray-500 truncate" data-id="element-5263">
                                  {d.phone} • ★ {d.rating}
                                </p>
                              </div>
                              {isCurrent && <span className="text-[10px] font-bold uppercase tracking-wider text-brand-600 shrink-0" data-id="element-5264">
                                  Selected
                                </span>}
                            </button>;
                })}
                    </motion.div>}
                </AnimatePresence>
              </>}
            <p className="text-xs text-gray-500 mt-1" data-id="element-5265">
              Only drivers without another truck are shown.
            </p>
          </div>

          {/* Services Capability */}
          <div data-id="element-5266">
            <div className="flex items-center justify-between mb-1" data-id="element-5267">
              <label className="block text-sm font-medium text-gray-700" data-id="element-5268">
                Capable Services
              </label>
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-5269">
                {truckServices.length} / {services.length} enabled
              </span>
            </div>
            <p className="text-xs text-gray-500 mb-2" data-id="element-5270">
              Select services this truck can perform. Applies to all{' '}
              <span className="font-semibold" data-id="element-5271">{type}</span> trucks.
            </p>
            {services.length === 0 ? <div className="text-xs text-gray-500 text-center py-4 border border-dashed border-gray-200 rounded-xl" data-id="element-5272">
                No services configured yet. Add services in the AI Service
                Advisor tab.
              </div> : <>
                <div className="flex items-center gap-2 mb-2" data-id="element-5273">
                  <button type="button" onClick={() => updateAutoDispatch({
                truckServiceMap: {
                  ...(autoDispatch.truckServiceMap || {}),
                  [type]: [...services]
                }
              })} className="text-xs font-medium text-gray-700 hover:text-gray-900 px-2 py-1 rounded-md hover:bg-gray-100" data-id="element-5274">
                    Select all
                  </button>
                  <button type="button" onClick={() => updateAutoDispatch({
                truckServiceMap: {
                  ...(autoDispatch.truckServiceMap || {}),
                  [type]: []
                }
              })} className="text-xs font-medium text-gray-700 hover:text-gray-900 px-2 py-1 rounded-md hover:bg-gray-100" data-id="element-5275">
                    Clear
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-1" data-id="element-5276">
                  {services.map(service => {
                const isSelected = truckServices.includes(service);
                return <button type="button" key={service} onClick={() => toggleService(service)} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors border ${isSelected ? 'bg-gray-900 text-white border-gray-900 hover:bg-gray-800' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'}`} data-id="element-5277">
                        {isSelected && <CheckCircle2 className="w-3 h-3" data-id="element-5278" />}
                        {service}
                      </button>;
              })}
                </div>
              </>}
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 shrink-0" data-id="element-5279">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl" data-id="element-5280">
            Cancel
          </button>
          <button onClick={handleSubmit} className="px-4 py-2 text-sm font-bold text-white bg-gray-900 hover:bg-gray-800 rounded-xl" data-id="element-5281">
            {mode === 'edit' ? 'Save Changes' : 'Add Truck'}
          </button>
        </div>
      </motion.div>
    </div>;
}
// ============ Assign Driver Modal ============
function AssignDriverModal({
  truck,
  drivers,
  trucks,
  onClose,
  onAssign
}: {
  truck: TruckType;
  drivers: ReturnType<typeof useSupplierStore>['drivers'];
  trucks: TruckType[];
  onClose: () => void;
  onAssign: (driverId: string | null) => void;
}) {
  const [search, setSearch] = useState('');
  const availableDrivers = drivers.filter(d => !d.assignedTruckId || d.assignedTruckId === truck.id);
  const filtered = availableDrivers.filter(d => d.name.toLowerCase().includes(search.toLowerCase()));
  const currentDriver = drivers.find(d => d.id === truck.assignedDriverId);
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" data-id="element-5282">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[80vh]" data-id="element-5283">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 shrink-0" data-id="element-5284">
          <div data-id="element-5285">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-5286">Assign Driver</h3>
            <p className="text-sm text-gray-500" data-id="element-5287">
              Truck {truck.plate} • {truck.type}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" data-id="element-5288">
            <X className="w-5 h-5" data-id="element-5289" />
          </button>
        </div>

        <div className="p-4 border-b border-gray-100 shrink-0" data-id="element-5290">
          <div className="relative" data-id="element-5291">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-5292" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search drivers..." className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-gray-900 focus:border-gray-900" data-id="element-5293" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto" data-id="element-5294">
          {currentDriver && <button onClick={() => onAssign(null)} className="w-full text-left px-6 py-3 border-b border-gray-100 hover:bg-red-50 group flex items-center gap-3" data-id="element-5295">
              <div className="w-9 h-9 rounded-full bg-red-100 text-red-600 flex items-center justify-center" data-id="element-5296">
                <X className="w-4 h-4" data-id="element-5297" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-5298">
                <p className="text-sm font-medium text-gray-900" data-id="element-5299">
                  Remove driver
                </p>
                <p className="text-xs text-gray-500 truncate" data-id="element-5300">
                  Currently: {currentDriver.name}
                </p>
              </div>
            </button>}
          {filtered.length === 0 ? <div className="p-8 text-center text-sm text-gray-500" data-id="element-5301">
              No available drivers
            </div> : filtered.map(d => {
          const isCurrent = d.id === truck.assignedDriverId;
          return <button key={d.id} onClick={() => onAssign(d.id)} className={`w-full text-left px-6 py-3 border-b border-gray-100 hover:bg-gray-50 flex items-center gap-3 ${isCurrent ? 'bg-brand-50/50' : ''}`} data-id="element-5302">
                  <div className="w-9 h-9 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center font-bold text-sm shrink-0" data-id="element-5303">
                    {d.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5304">
                    <p className="text-sm font-medium text-gray-900 truncate" data-id="element-5305">
                      {d.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate" data-id="element-5306">
                      {d.phone} • ★ {d.rating}
                    </p>
                  </div>
                  {isCurrent && <span className="text-[10px] font-bold uppercase tracking-wider text-brand-600" data-id="element-5307">
                      Current
                    </span>}
                </button>;
        })}
        </div>
      </motion.div>
    </div>;
}
// ============ Confirm Delete Modal ============
function ConfirmDeleteModal({
  truck,
  onCancel,
  onConfirm
}: {
  truck: TruckType;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" data-id="element-5308">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden" data-id="element-5309">
        <div className="p-6" data-id="element-5310">
          <div className="w-12 h-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-4" data-id="element-5311">
            <Trash2 className="w-5 h-5" data-id="element-5312" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-5313">
            Remove this truck?
          </h3>
          <p className="text-sm text-gray-600" data-id="element-5314">
            Truck <span className="font-bold" data-id="element-5315">{truck.plate}</span> ({truck.type}
            ) will be removed from your fleet. Any active jobs assigned to it
            will be unassigned. This cannot be undone.
          </p>
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3" data-id="element-5316">
          <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl" data-id="element-5317">
            Cancel
          </button>
          <button onClick={onConfirm} className="px-4 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl" data-id="element-5318">
            Remove Truck
          </button>
        </div>
      </motion.div>
    </div>;
}
// ============ Bulk Upload Modal ============
type ParsedRow = {
  rowNumber: number;
  plate: string;
  type: string;
  capacity: string;
  status: TruckType['status'];
  lastService: string;
  errors: string[];
};
function parseCSV(text: string): string[][] {
  // Simple CSV parser: handles quoted fields with commas
  const rows: string[][] = [];
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
  for (const line of lines) {
    const fields: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (c === ',' && !inQuotes) {
        fields.push(current.trim());
        current = '';
      } else {
        current += c;
      }
    }
    fields.push(current.trim());
    rows.push(fields);
  }
  return rows;
}
function BulkUploadModal({
  onClose,
  onImport
}: {
  onClose: () => void;
  onImport: (rows: ParsedRow[]) => void;
}) {
  const [step, setStep] = useState<'upload' | 'preview'>('upload');
  const [fileName, setFileName] = useState<string>('');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleDownloadTemplate = () => {
    const headers = ['plate', 'type', 'capacity', 'status', 'lastService'];
    const sampleRows = [['D 12345', '3-Ton Box Truck', '3 Tons', 'Active', '2024-10-15'], ['S 67890', 'Flatbed 12m', '12m flatbed', 'Idle', '2024-09-20'], ['A 11223', 'Refrigerated 7-Ton', '7 Tons reefer', 'Active', '2024-11-05']];
    const csv = [headers.join(','), ...sampleRows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'fleet-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };
  const handleFileSelect = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      const rows = parseCSV(text);
      if (rows.length < 2) {
        toast.error('CSV file is empty or missing data rows');
        return;
      }
      const headers = rows[0].map(h => h.toLowerCase());
      const plateIdx = headers.indexOf('plate');
      const typeIdx = headers.indexOf('type');
      const capacityIdx = headers.indexOf('capacity');
      const statusIdx = headers.indexOf('status');
      const lastServiceIdx = headers.indexOf('lastservice');
      if (plateIdx === -1 || typeIdx === -1 || capacityIdx === -1) {
        toast.error('CSV must include columns: plate, type, capacity, status, lastService');
        return;
      }
      const validStatuses: TruckType['status'][] = ['Active', 'In service', 'Idle', 'Maintenance'];
      const parsed: ParsedRow[] = rows.slice(1).map((row, idx) => {
        const errors: string[] = [];
        const plate = row[plateIdx]?.toUpperCase() || '';
        const type = row[typeIdx] || '';
        const capacity = row[capacityIdx] || '';
        const statusRaw = row[statusIdx] || 'Idle';
        const lastService = row[lastServiceIdx] || '';
        if (!plate) errors.push('Plate required');
        if (!type) errors.push('Type required');
        if (!capacity) errors.push('Capacity required');
        const matchedStatus = validStatuses.find(s => s.toLowerCase() === statusRaw.toLowerCase());
        if (statusRaw && !matchedStatus) {
          errors.push(`Invalid status "${statusRaw}"`);
        }
        return {
          rowNumber: idx + 2,
          plate,
          type,
          capacity,
          status: matchedStatus || 'Idle',
          lastService: lastService || new Date().toISOString().slice(0, 10),
          errors
        };
      });
      setParsedRows(parsed);
      setStep('preview');
    };
    reader.readAsText(file);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };
  const validRows = parsedRows.filter(r => r.errors.length === 0);
  const invalidRows = parsedRows.filter(r => r.errors.length > 0);
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" data-id="element-5319">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-5320">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 shrink-0" data-id="element-5321">
          <div data-id="element-5322">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-5323">
              <Upload className="w-5 h-5 text-blue-600" data-id="element-5324" />
              Bulk Upload Trucks
            </h3>
            <p className="text-sm text-gray-500" data-id="element-5325">
              {step === 'upload' ? 'Upload a CSV file to import multiple trucks at once' : `Review ${parsedRows.length} row${parsedRows.length !== 1 ? 's' : ''} before importing`}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" data-id="element-5326">
            <X className="w-5 h-5" data-id="element-5327" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1" data-id="element-5328">
          {step === 'upload' ? <div className="space-y-6" data-id="element-5329">
              {/* Template download */}
              <div className="flex items-start gap-4 p-4 bg-blue-50/50 border border-blue-100 rounded-xl" data-id="element-5330">
                <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center shrink-0" data-id="element-5331">
                  <FileSpreadsheet className="w-5 h-5" data-id="element-5332" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-5333">
                  <h4 className="text-sm font-bold text-gray-900" data-id="element-5334">
                    New here? Start with our template
                  </h4>
                  <p className="text-xs text-gray-600 mt-0.5" data-id="element-5335">
                    Download the CSV template with sample rows and the exact
                    column format we expect.
                  </p>
                </div>
                <button onClick={handleDownloadTemplate} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-200 text-blue-700 rounded-lg text-xs font-bold hover:bg-blue-50 transition-colors" data-id="element-5336">
                  <Download className="w-3.5 h-3.5" data-id="element-5337" />
                  Download
                </button>
              </div>

              {/* Upload area */}
              <div onDragOver={e => {
            e.preventDefault();
            setDragOver(true);
          }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${dragOver ? 'border-blue-400 bg-blue-50/50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-5338">
                <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={e => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }} data-id="element-5339" />
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" data-id="element-5340" />
                <p className="text-sm font-bold text-gray-900" data-id="element-5341">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-5342">CSV file, max 5MB</p>
              </div>

              {/* Required columns hint */}
              <div className="text-xs text-gray-500" data-id="element-5343">
                <p className="font-semibold text-gray-700 mb-1" data-id="element-5344">
                  Required columns:
                </p>
                <div className="flex flex-wrap gap-1.5" data-id="element-5345">
                  {['plate', 'type', 'capacity', 'status', 'lastService'].map(c => <span key={c} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded font-mono" data-id="element-5346">
                        {c}
                      </span>)}
                </div>
              </div>
            </div> : <div className="space-y-4" data-id="element-5347">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-3" data-id="element-5348">
                <div className="p-3 bg-green-50 border border-green-100 rounded-xl" data-id="element-5349">
                  <div className="flex items-center gap-2 mb-1" data-id="element-5350">
                    <CheckCircle2 className="w-4 h-4 text-green-600" data-id="element-5351" />
                    <p className="text-xs font-bold text-green-700 uppercase tracking-wider" data-id="element-5352">
                      Ready to import
                    </p>
                  </div>
                  <p className="text-2xl font-display font-bold text-gray-900" data-id="element-5353">
                    {validRows.length}
                  </p>
                </div>
                <div className={`p-3 border rounded-xl ${invalidRows.length > 0 ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`} data-id="element-5354">
                  <div className="flex items-center gap-2 mb-1" data-id="element-5355">
                    <AlertCircle className={`w-4 h-4 ${invalidRows.length > 0 ? 'text-red-600' : 'text-gray-400'}`} data-id="element-5356" />
                    <p className={`text-xs font-bold uppercase tracking-wider ${invalidRows.length > 0 ? 'text-red-700' : 'text-gray-500'}`} data-id="element-5357">
                      Has errors
                    </p>
                  </div>
                  <p className="text-2xl font-display font-bold text-gray-900" data-id="element-5358">
                    {invalidRows.length}
                  </p>
                </div>
              </div>

              <div className="text-xs text-gray-500" data-id="element-5359">
                File:{' '}
                <span className="font-medium text-gray-700" data-id="element-5360">{fileName}</span>
              </div>

              {/* Preview table */}
              <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-5361">
                <div className="max-h-80 overflow-y-auto" data-id="element-5362">
                  <table className="w-full text-xs" data-id="element-5363">
                    <thead className="bg-gray-50 text-gray-500 uppercase tracking-wider sticky top-0" data-id="element-5364">
                      <tr data-id="element-5365">
                        <th className="px-3 py-2 text-left font-bold" data-id="element-5366">#</th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-5367">Plate</th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-5368">Type</th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-5369">
                          Capacity
                        </th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-5370">
                          Status
                        </th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-5371">
                          Issues
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100" data-id="element-5372">
                      {parsedRows.map(row => <tr key={row.rowNumber} className={row.errors.length > 0 ? 'bg-red-50/50' : ''} data-id="element-5373">
                          <td className="px-3 py-2 text-gray-500 font-mono" data-id="element-5374">
                            {row.rowNumber}
                          </td>
                          <td className="px-3 py-2 font-medium text-gray-900" data-id="element-5375">
                            {row.plate || <span className="text-red-500 italic" data-id="element-5376">empty</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-700" data-id="element-5377">
                            {row.type || <span className="text-red-500 italic" data-id="element-5378">empty</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-700" data-id="element-5379">
                            {row.capacity || <span className="text-red-500 italic" data-id="element-5380">empty</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-700" data-id="element-5381">
                            {row.status}
                          </td>
                          <td className="px-3 py-2" data-id="element-5382">
                            {row.errors.length === 0 ? <span className="inline-flex items-center gap-1 text-green-600" data-id="element-5383">
                                <CheckCircle2 className="w-3 h-3" data-id="element-5384" /> OK
                              </span> : <span className="text-red-600" data-id="element-5385">
                                {row.errors.join(', ')}
                              </span>}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>}
        </div>

        <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-between items-center shrink-0" data-id="element-5386">
          {step === 'preview' ? <button onClick={() => {
          setStep('upload');
          setParsedRows([]);
          setFileName('');
        }} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 bg-gray-100 rounded-xl transition-colors" data-id="element-5387">
              Back
            </button> : <div data-id="element-5388"></div>}
          <div className="flex gap-3" data-id="element-5389">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl" data-id="element-5390">
              Cancel
            </button>
            {step === 'preview' && <button onClick={() => onImport(validRows)} disabled={validRows.length === 0} className="px-4 py-2 text-sm font-bold text-white bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-xl" data-id="element-5391">
                Import {validRows.length} truck
                {validRows.length !== 1 ? 's' : ''}
              </button>}
          </div>
        </div>
      </motion.div>
    </div>;
}